import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { apiClient } from '../lib/api';
import { ArrowLeft, Sparkles } from 'lucide-react';

export default function NewProject() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    site_url: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post('/projects', formData);
      toast.success('Projet créé avec succès !');
      navigate(`/projects/${response.data.id}/calendar`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de la création du projet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <header className="border-b bg-white/70 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-heading font-bold">Nouveau Projet</h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Créer un nouveau projet</CardTitle>
            <CardDescription>
              Configurez votre moteur marketing IA. Chaque projet est complètement isolé et possède sa propre automatisation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nom du projet *</Label>
                <Input
                  id="name"
                  placeholder="Ex: Blog Entreprise, E-commerce Mode..."
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="project-name-input"
                />
                <p className="text-xs text-muted-foreground">Un nom descriptif pour identifier votre projet</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="site_url">URL du site *</Label>
                <Input
                  id="site_url"
                  type="url"
                  placeholder="https://votre-site.com"
                  value={formData.site_url}
                  onChange={(e) => setFormData({ ...formData, site_url: e.target.value })}
                  required
                  data-testid="project-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  L'IA analysera ce site pour générer du contenu adapté
                </p>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Ce qui se passe ensuite :</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• L'IA va analyser votre site</li>
                  <li>• Un calendrier éditorial sera généré</li>
                  <li>• Vous pourrez valider chaque contenu avant publication</li>
                  <li>• L'automatisation reste OFF par défaut</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 rounded-full"
                  data-testid="cancel-button"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 rounded-full"
                  data-testid="create-project-submit-button"
                >
                  {loading ? 'Création...' : 'Créer le projet'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}